"use client";

import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { OpenAIKeyModal } from "@/react/components/entityViews/settings/components/OpenAIKeyModal";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { useBoardPermissions } from "@/react/components/entityViews/settings/hooks/useBoardPermissions";
import { VIEW_TYPES } from "@/other/constants/OtherKeys";
import { INTEGRATION_SETTING_KEYS } from "./constants";
import { registerIntegrationFixHandlers } from "./reportIntegrationToast";

type OpenAIKeyModalOpenOptions = {
  onSaved?: () => void;
};

type OpenAIKeyModalContextValue = {
  open: (options?: OpenAIKeyModalOpenOptions) => void;
  close: () => void;
};

const OpenAIKeyModalContext = createContext<OpenAIKeyModalContextValue | null>(null);

const IntegrationFixRegistrar = ({ openOpenAiKeyModal }: { openOpenAiKeyModal: () => void }) => {
  const { selectedBoard, viewManager } = useSwivuBryntumContext();
  const { canManageSecrets, canManageCompanyCam } = useBoardPermissions();

  const canOpenCompanyCamSettings = Boolean(canManageCompanyCam && selectedBoard?.record && viewManager);

  const openCompanyCamSettings = useCallback(() => {
    if (!viewManager || !canManageCompanyCam) return;

    viewManager.openAnonymousViewByType({
      viewType: VIEW_TYPES.settings,
      viewName: "Settings",
      otherParams: {
        setting: INTEGRATION_SETTING_KEYS.company_cam,
      },
    });
  }, [viewManager, canManageCompanyCam]);

  useEffect(() => {
    registerIntegrationFixHandlers({
      openOpenAiKeyModal,
      openIntegrationSettings: (provider) => {
        if (provider === "company_cam") {
          openCompanyCamSettings();
        }
      },
      canFixOpenAi: canManageSecrets,
      canFixCompanyCam: canOpenCompanyCamSettings,
    });
  }, [openOpenAiKeyModal, openCompanyCamSettings, canManageSecrets, canOpenCompanyCamSettings]);

  return null;
};

export const OpenAIKeyModalProvider = ({ children }: { children: ReactNode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const onSavedRef = useRef<(() => void) | null>(null);

  const open = useCallback((options?: OpenAIKeyModalOpenOptions) => {
    onSavedRef.current = options?.onSaved ?? null;
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    onSavedRef.current = null;
  }, []);

  const handleSaved = useCallback(() => {
    onSavedRef.current?.();
    onSavedRef.current = null;
    setIsOpen(false);
  }, []);

  return (
    <OpenAIKeyModalContext.Provider value={{ open, close }}>
      <IntegrationFixRegistrar openOpenAiKeyModal={() => open()} />
      {children}
      <OpenAIKeyModal isOpen={isOpen} onClose={close} onSaved={handleSaved} stackAboveModal />
    </OpenAIKeyModalContext.Provider>
  );
};

export const useOpenAIKeyModal = () => useContext(OpenAIKeyModalContext);
